import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { F921 } from '../../services/p922aApi';
import { useP922AMode } from './hooks/useP922AMode';
import { useLookupNames } from './hooks/useLookupNames';
import { useP922ARecord } from './hooks/useP922ARecord';
import { useP922ANavigation } from './hooks/useP922ANavigation';
import { useP922APermissions } from './hooks/useP922APermissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2MonthDetail from './components/Tab2MonthDetail';
import ActionBar from './components/ActionBar';
import { getApprovalStatus, isFieldDisabledFn } from './helpers';
import './P922AForm.css';

// ── Component ────────────────────────────────────────────────
const P922AForm: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // ── Hooks ──
  const perms = useP922APermissions();
  const { mode, setMode, isEditing } = useP922AMode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
  } = useP922ARecord();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP922ANavigation(loadRecordWithNames);

  // ── Initial Load ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load P922A list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // ── Mode Actions ──
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: F921) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.f921_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    // BR-V07: cannot delete approved/rejected
    if (record.f921_30 === 'Y' || record.f921_30 === 'N') {
      message.error('已核准/不准假之請假單不可刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除請假單 ${record.f921_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  // ── Computed Values ──
  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing, mode), [isEditing, mode]);
  const statusBadge = getApprovalStatus(record);

  // ── Render ─────────────────────────────────────────────────
  return (
    <PermissionGuard module={Modules.P922A} action="query">
    <div className="p922a-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>人事管理</a>
              <span>/</span>
              <span className="current">請假單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>請假單</h1>
            <span className="badge badge-default">922A</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>單號</label>
            <input
              value={String(formData.f921_01 || '')}
              disabled
              readOnly
              style={{ width: 100 }}
            />
          </div>
          <div className="field-group">
            <label>員工</label>
            <span style={{ fontSize: 13 }}>
              {String(formData.f921_02 || '')} {names.employee ? `(${names.employee})` : ''}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '月份明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 基本資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: 月份明細 (F9211) */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2MonthDetail
              details={details}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
            />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default P922AForm;
